import {
  ArrayUtils,
  DateUtils,
  IconHeaderWithSearch,
  StringUtils,
  ConfigObjectType,
  ExportUtils,
  useCurrentLocale,
  NoDataMessageWithIcon,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  CHART_MONTH_TICKFORMAT,
  ChartColor,
  DEFAULT_PAGE_SIZE,
  Formatters,
  getDefaultMTOptions,
  getLineChartItems,
  getTableMaxHeight,
  HaidiSchema,
  JustTooltipFormatter,
  LightChart,
  LightChartImageDownloadProps,
  LightChartLayout,
  LightChartLegend2,
  LightLineChartTrace,
  LightSelectedFilters,
  MaterialTableWrapper,
  ProgressBarFormatter,
  TableSorting,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {get} from 'lodash';
import {Column} from 'material-table';
import React, {useMemo, useCallback} from 'react';
import {useIntl} from 'react-intl';
import {filterLegendByHaiState, markChartDataLegendByHaiState} from './reportLegendUtils';
import {useChartHiddenMap} from '../../common/useChartHiddenMap';

export type HaiYearlyOverviewReportData = HaidiSchema.components['schemas']['HaiYearlyOverviewReportDto'];
type WithTotal<T> = T & {isTotalRow?: boolean};

type HaisByTypeRowType = HaiYearlyOverviewReportData['monthSummaries'][0];
type HaisByTypeRowTypeWithTotal = WithTotal<HaisByTypeRowType>;

const EMPTY = '-';

function useHaisByTypeColumns(formatters: Formatters, defaultSort: TableSorting): Column<HaisByTypeRowTypeWithTotal>[] {
  const {t, formatFloat, parseDate} = formatters;
  const intl = useIntl();

  const columns: Column<HaisByTypeRowTypeWithTotal>[] = [
    {
      field: 'month',
      title: t('filter.hai.month'),
      render: (data) => {
        const month = data.isTotalRow ? t('reports.totalRow') : parseDate(data.month).toLocaleDateString(intl.locale, {month: 'long'});
        return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={month ?? EMPTY} tooltipOverride={month} bold={data.isTotalRow} />;
      },
      sorting: true,
    },
    {
      field: 'summaryDto.measureCount',
      title: t('reports.haiCountPerDepartment.haiCount'),
      render: (data) => {
        const measureCount = formatFloat(data.summaryDto.measureCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={measureCount ?? EMPTY}
            tooltipOverride={measureCount}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'summaryDto.measureHospitalizationPercentage',
      title: t('reports.haiCountPerDepartment.hospWithHaiPercentage'),
      render: (data) => {
        const percent = data.summaryDto.measureHospitalizationPercentage ?? 0;
        const percentString = `${formatFloat(percent)} %`;
        return (
          <ProgressBarFormatter
            {...AG_GRID_COMPATIBILITY}
            value={percentString ?? EMPTY}
            tooltipOverride={percentString}
            bold={data.isTotalRow}
            progressPercent={percent}
            style={{maxWidth: '150px'}}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'summaryDto.hospitalizationCount',
      title: t('reports.haiCountPerDepartment.hospCount'),
      render: (data) => {
        const hospitalizationCount = formatFloat(data.summaryDto.hospitalizationCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={hospitalizationCount ?? EMPTY}
            tooltipOverride={hospitalizationCount}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'summaryDto.careDaysCount',
      title: t('reports.haiCountPerDepartment.careDaysCount'),
      render: (data) => {
        const careDaysCount = formatFloat(data.summaryDto.careDaysCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={careDaysCount ?? EMPTY}
            tooltipOverride={careDaysCount}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'summaryDto.measureCountPer1000HospitalizationDays',
      title: t('reports.haiCountPerDepartment.haiCountPer1000CareDays'),
      render: (data) => {
        const measureCountPer1000HospitalizationDays = formatFloat(data.summaryDto.measureCountPer1000HospitalizationDays);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={measureCountPer1000HospitalizationDays ?? EMPTY}
            tooltipOverride={measureCountPer1000HospitalizationDays}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'summaryDto.deathCausedByHaiCount',
      title: t('reports.haiCountPerDepartment.deathDueToHaiCount'),
      render: (data) => {
        const deathCausedByHaiCount = formatFloat(data.summaryDto.deathCausedByHaiCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={deathCausedByHaiCount ?? EMPTY}
            tooltipOverride={deathCausedByHaiCount}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort:
      v.customSort ??
      ((row1, row2, _type) => {
        const isSortAscending = defaultSort.direction === 'asc';
        if (row1.isTotalRow) return isSortAscending ? -1 : 1;
        if (row2.isTotalRow) return isSortAscending ? 1 : -1;
        const key = (row: HaisByTypeRowTypeWithTotal) => get(row, v.field ?? '');
        return StringUtils.compare(key(row1), key(row2));
      }),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

type Props = {
  muiStyles: any;
  storageIdPrefix: string;
  filters: any;
  changeFilters: (newFilters: any) => void;
  hasTopSorting: boolean;
  handleResetTopSorting: () => void;
  hasFilters: boolean;
  handleResetFilters: () => void;
  formatters: Formatters;
  isLoading: boolean;
  reportData: HaiYearlyOverviewReportData | undefined;
  config: ConfigObjectType;
};
export const HaiYearlyOverviewReportTableAndCharts: React.FC<Props> = (props) => {
  const {
    muiStyles,
    storageIdPrefix: _,
    filters,
    changeFilters,
    hasTopSorting,
    handleResetTopSorting,
    hasFilters,
    handleResetFilters,
    formatters,
    isLoading,
    reportData,
    config,
  } = props;
  const {tEnum, t} = formatters;
  const haisByTypeColumns = useHaisByTypeColumns(formatters, filters.haisByTypeSort);
  const haisByTypeData: HaisByTypeRowTypeWithTotal[] = useMemo(() => {
    const totalRow: HaisByTypeRowTypeWithTotal = {
      month: '',
      summaryDto: reportData?.yearTotal ?? {
        hospitalizationCount: 0,
        careDaysCount: 0,
        measureHospitalizationPercentage: 0,
        measureCountPer1000HospitalizationDays: 0,
        measureCount: 0,
        deathCausedByHaiCount: 0,
      },
    };
    totalRow.isTotalRow = true;
    return [totalRow, ...(reportData?.monthSummaries ?? [])];
  }, [reportData]);

  const chartLayout: LightChartLayout = useMemo(() => {
    return {
      margin: {
        b: 42,
      },
      xaxis: {
        type: 'date',
        tickformat: CHART_MONTH_TICKFORMAT,
        automargin: true,
      },
      yaxis: {
        rangemode: 'tozero',
        automargin: true,
        fixedrange: true,
      },
      height: 350,
      showlegend: false,
    };
  }, []);
  const chartImageDownload: LightChartImageDownloadProps = {
    showlegend: true,
    title: {
      text: t('reports.yearlyOverview.progressOverTime'),
    },
  };
  const {hiddenMap, onSetHiddenMap, applyHidden} = useChartHiddenMap();
  const chartColors: ChartColor[] = useMemo(() => {
    return applyHidden([
      {
        id: 'YES',
        color: '#FF8717',
        label: tEnum('State', 'YES'),
      },
      {
        id: 'COL',
        color: '#6FA9FF',
        label: tEnum('State', 'COL'),
      },
    ]);
  }, [tEnum, applyHidden]);
  const legendItems = useMemo(() => filterLegendByHaiState(chartColors, filters.report.haiState), [chartColors, filters.report.haiState]);
  const parseChartData = (v: HaiYearlyOverviewReportData['confirmedHaisByMonth'][0]) => ({
    timestamp: DateUtils.fromReverseMonthFormat(v.month ?? '').toISOString(),
    count: v.count ?? 0,
    state: v.state,
  });
  // Month buckets may arrive out of chronological order; sort ascending so the line doesn't jump backwards.
  const byTimestampAsc = (a: {timestamp: string}, b: {timestamp: string}) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  const unboundChartData = ArrayUtils.zip(
    (reportData?.confirmedHaisByMonth ?? []).map(parseChartData).sort(byTimestampAsc),
    (reportData?.colonizedHaisByMonth ?? []).map(parseChartData).sort(byTimestampAsc)
  );
  const allCountsZero = unboundChartData.every((arr) => arr.every((obj) => obj?.count === 0));
  const dataChartColors = useMemo(
    () => markChartDataLegendByHaiState(chartColors, filters.report.haiState),
    [chartColors, filters.report.haiState]
  );
  const chartData: LightLineChartTrace[] = useMemo(() => {
    const {formatFloat} = formatters;
    return getLineChartItems(allCountsZero ? [] : unboundChartData, dataChartColors, (v, i) => ({
      x: v[i]?.timestamp ?? '',
      y: v[i]?.count ?? 0,
      tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${dataChartColors[i].label}</b>: ${formatFloat(
        v[i]?.count ?? 0
      )}`,
    })).map((v) => ({...v, cliponaxis: false}));
  }, [unboundChartData, dataChartColors, formatters]); // eslint-disable-line react-hooks/exhaustive-deps
  const currentLocale = useCurrentLocale();
  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.haiXlsxExport.replace('{reportName}', 'HAI_YEARLY_OVERVIEW_REPORT'),
      filters: filters.report,
      locale: currentLocale,
    });
  }, [config.service.url.haiXlsxExport, currentLocale, filters]);
  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);
  return (
    <React.Fragment key='HaiYearlyOverviewReportTableAndCharts'>
      <div css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={'reports.yearlyOverview.yearlyOverviewHai'}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
        >
          <LightSelectedFilters
            hasFilters={hasFilters}
            handleResetFilters={handleResetFilters}
            hasSorting={hasTopSorting}
            handleResetSorting={handleResetTopSorting}
            hasXlsxExport
            handleXlsxExport={() => downloadXlsxExport()}
          />
        </IconHeaderWithSearch>
        <MaterialTableWrapper
          style={{flex: 5}}
          options={{
            ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
            paging: false,
          }}
          variant='big'
          totalRow='top'
          useMaxHeight
          columns={haisByTypeColumns}
          data={haisByTypeData}
          isLoading={isLoading}
          onOrderChange={(colIndex, direction) => {
            const columnId = haisByTypeColumns[colIndex].field as string;
            changeFilters({
              haisByTypeSort: {
                field: columnId,
                direction,
              },
            });
          }}
          controlledSort={(() => {
            return filters.haisByTypeSort;
          })()}
        />
      </div>
      <div css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={'reports.yearlyOverview.progressOverTime'}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
        >
          <LightChartLegend2 items={legendItems} hiddenMap={hiddenMap} onSetHiddenMap={onSetHiddenMap} />
        </IconHeaderWithSearch>
        <LightChart
          key={JSON.stringify(chartLayout.xaxis.range)}
          data={chartData}
          layout={chartLayout}
          config={{}}
          imageDownload={chartImageDownload}
          noDataComponent={noDataComponent}
        />
      </div>
    </React.Fragment>
  );
};
